import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lightbulb } from "lucide-react";
import { getAuthToken } from "@/lib/auth";

export interface Question {
  id: string;
  question: string;
  options: string[];
  correctAnswer?: number;
  explanation?: string;
  category: string;
  language: string;
  userSelected?: number | null; // ✅ allow null for unattempted
}

interface ReviewProps {
  sessionId: string; 
  onClose: () => void;
}

const apiURL = import.meta.env.VITE_API_URL || "http://localhost:7845";

export function Review({ sessionId, onClose }: ReviewProps) {
  const { data: questions, isLoading } = useQuery<Question[]>({
    queryKey: ["sessionQuestions", sessionId],
    queryFn: async () => {
      const token = getAuthToken();
      const res = await fetch(`${apiURL}/api/examination/session/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch session questions");
      return res.json();
    },
  });

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="p-6">Loading questions...</Card>
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <Card className="p-6">
          No questions available.
          <div className="mt-4 text-right">
            <Button onClick={onClose}>Close</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-auto z-50">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg w-11/12 max-w-4xl mx-auto my-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Review Session</h2>
          <Button onClick={onClose}>Close</Button>
        </div>

        <div className="space-y-6">
          {questions.map((q, index) => {
            const userAnswer = q.userSelected ?? null;

            const optionClass = (idx: number) => {
              let base = "w-full text-left px-4 py-3 rounded-lg border transition";
              if (idx === q.correctAnswer) base += " bg-green-100 border-green-500 text-green-800";
              if (userAnswer !== null && idx === userAnswer && userAnswer !== q.correctAnswer)
                base += " bg-red-100 border-red-500 text-red-800";
              return base;
            };

            return (
              <Card key={q.id} className="p-6">
                <div className="flex justify-between items-center mb-2">
                  <span>Question {index + 1} of {questions.length}</span>
                  {userAnswer !== null ? (
                    <span className="px-2 py-1 text-xs font-semibold bg-green-200 text-green-800 rounded-full">
                      Attempted
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-800 rounded-full">
                      Not Attempted
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-semibold mb-4">{q.question}</h3>
                <div className="space-y-2 mb-4">
                  {q.options.map((opt, idx) => (
                    <div key={idx} className={optionClass(idx)}>
                      {String.fromCharCode(65 + idx)}) {opt}
                      {userAnswer === idx && idx === q.correctAnswer && (
                        <span className="ml-2 text-green-700">(Your answer, Correct)</span>
                      )}
                      {userAnswer === idx && idx !== q.correctAnswer && (
                        <span className="ml-2 text-red-700">(Your answer, Wrong)</span>
                      )}
                      {userAnswer === null && idx === q.correctAnswer && (
                        <span className="ml-2 text-blue-700">(Correct answer, you did not attempt)</span>
                      )}
                      {idx === q.correctAnswer && userAnswer !== idx && userAnswer !== null && (
                        <span className="ml-2 text-green-700">(Correct answer)</span>
                      )}
                    </div>
                  ))}
                </div>

                {q.explanation && (
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                    <h4 className="font-semibold flex items-center text-blue-600 mb-2">
                      <Lightbulb size={18} className="mr-2" /> Explanation
                    </h4>
                    <p className="text-blue-700">{q.explanation}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
